import React, { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { translate } from '@utils/i18n';
import { Text } from '@ui';
import { Tag } from '@ui/atoms/Tag';
import { Accordion } from '@ui/molecules/Accordion';
import { useIpaInspectionAssessment } from '../../hooks/useIpaInspectionAssessment';
import { useIssueBucketCounts } from '../../hooks/useIssueBucketCounts';
import { iimReviewKeys } from '../../hooks/queryKeys';
import { IIM_RUN_COMPLETED_EVENT } from '../../events';
import { TechnicianRowList } from '../TechnicianRow';
import IssueTypeBucket from '../IssueTypeBucket';
import AssessmentFeedback from '../AssessmentFeedback';
import LegacyAiSummaryBanner from './LegacyAiSummaryBanner';
import './AiSummaryBanner.scss';
const NS = 'features.iim_review.ai_summary_banner';
const ASSESSMENT_NS = 'features.iim_review.ipa.inspection_assessment';
const BUCKET_SEVERITY_ORDER = [
    'finish_inspection',
    'resolve_building_issues',
    'verify_inspection_data',
    'verify_code_requirements',
    'verify_inspection_activity',
    'check_company_rules'
];
/* eslint-disable @typescript-eslint/naming-convention -- IpaInspectionAssessmentRating values are snake_case */
const RATING_LABEL_DEFAULTS = {
    high_priority: 'High Priority',
    medium_priority: 'Medium Priority',
    low_priority: 'Low Priority'
};
const RATING_TAG_VARIANT = {
    high_priority: 'red',
    medium_priority: 'yellow',
    low_priority: 'green'
};
/* eslint-enable @typescript-eslint/naming-convention */
function ratingLabel(rating) {
    return translate(`rating_${rating}`, { namespace: NS, defaultValue: RATING_LABEL_DEFAULTS[rating] });
}
function IpaSummaryBanner({ inspectionId, ipaScorecardEnabled = false }) {
    const { data: issueCounts } = useIssueBucketCounts(inspectionId);
    const { bucketCounts, typeCounts, primaryTechnician, assetCountChangeDetail } = issueCounts !== null && issueCounts !== void 0 ? issueCounts : {};
    const { data: assessment, refetch } = useIpaInspectionAssessment(inspectionId);
    const queryClient = useQueryClient();
    useEffect(() => {
        const handler = (event) => {
            const { detail } = event;
            if (detail.inspectionId !== inspectionId)
                return;
            void refetch();
            void queryClient.invalidateQueries({ queryKey: iimReviewKeys.bucketCounts(inspectionId) });
        };
        document.addEventListener(IIM_RUN_COMPLETED_EVENT, handler);
        return () => document.removeEventListener(IIM_RUN_COMPLETED_EVENT, handler);
    }, [inspectionId, refetch, queryClient]);
    const activeBuckets = BUCKET_SEVERITY_ORDER.filter((key) => { var _a; return ((_a = bucketCounts === null || bucketCounts === void 0 ? void 0 : bucketCounts[key]) !== null && _a !== void 0 ? _a : 0) > 0; });
    const totalCount = activeBuckets.reduce((sum, key) => { var _a; return sum + ((_a = bucketCounts === null || bucketCounts === void 0 ? void 0 : bucketCounts[key]) !== null && _a !== void 0 ? _a : 0); }, 0);
    const hasBuckets = activeBuckets.length > 0;
    const isInProgress = ipaScorecardEnabled && ((assessment === null || assessment === void 0 ? void 0 : assessment.status) === 'pending' || (assessment === null || assessment === void 0 ? void 0 : assessment.status) === 'processing');
    const isComplete = ipaScorecardEnabled && (assessment === null || assessment === void 0 ? void 0 : assessment.status) === 'complete';
    if (!hasBuckets && !isInProgress && !isComplete)
        return null;
    const title = (React.createElement(Text, { variant: "small", as: "span", className: "ai-summary-banner__title-text" }, translate('header_title', { namespace: NS, defaultValue: 'Inspection Priority Assessment' })));
    const actionsContent = isComplete && assessment && (React.createElement("span", { className: "ai-summary-banner__priority" },
        React.createElement(Text, { variant: "x-small", as: "span", className: "ai-summary-banner__priority-label" }, translate('overall_priority', { namespace: ASSESSMENT_NS, defaultValue: 'Overall Priority:' })),
        React.createElement(Tag, { label: ratingLabel(assessment.rating), variant: RATING_TAG_VARIANT[assessment.rating], colorStyle: "solid", readOnly: true })));
    return (React.createElement(Accordion, { title: title, actionsContent: actionsContent, variant: "inline", defaultExpanded: true, className: "ai-summary-banner" },
        React.createElement("div", { className: "ai-summary-banner__body" },
            isInProgress && (React.createElement(Text, { variant: "body", as: "p" }, translate('processing', {
                namespace: ASSESSMENT_NS,
                defaultValue: 'Peer analysis is still processing.'
            }))),
            isComplete && (assessment === null || assessment === void 0 ? void 0 : assessment.summary) && (React.createElement(Text, { variant: "body", as: "p", className: "ai-summary-banner__summary" }, assessment.summary)),
            (hasBuckets || ipaScorecardEnabled) && (React.createElement("div", { className: "ai-summary-banner__buckets" },
                activeBuckets.map((bucketKey) => {
                    var _a;
                    return (React.createElement(IssueTypeBucket, { key: bucketKey, bucketKey: bucketKey, count: (_a = bucketCounts === null || bucketCounts === void 0 ? void 0 : bucketCounts[bucketKey]) !== null && _a !== void 0 ? _a : 0, totalCount: totalCount, typeCounts: typeCounts, technician: bucketKey === 'finish_inspection' ? primaryTechnician : undefined, assetCountChangeDetail: bucketKey === 'verify_inspection_data' ? assetCountChangeDetail : undefined }));
                }),
                ipaScorecardEnabled && React.createElement(TechnicianRowList, { inspectionId: inspectionId }))),
            isComplete && assessment && React.createElement(AssessmentFeedback, { assessmentId: assessment.id }))));
}
// TEMP(tmp_hide_new_ai_summary): remove when IP-15683 ships. The six-bucket /
// scorecard UI is unreleased, so tenants without ia_ipa_subprocesses_enabled get
// the master written-summary banner. Branching here (not inside IpaSummaryBanner)
// also skips its issues/assessment fetches when the flag is off.
function AiSummaryBanner({ inspectionId, ipaScorecardEnabled = false }) {
    if (!ipaScorecardEnabled)
        return React.createElement(LegacyAiSummaryBanner, { inspectionId: inspectionId });
    return React.createElement(IpaSummaryBanner, { inspectionId: inspectionId, ipaScorecardEnabled: true });
}
export default AiSummaryBanner;
